import React from "react";
import { Icon } from "@iconify/react";
import { Box, HStack, VStack } from "../utils";

export type Step = {
  id?: string | number;
  stepNo: number;
  title: string;
  description: string;
  disabled?: boolean;
  children?: Step[];
};

export type StepperProps = {
  steps: Step[];
  currentStep: number;
  onStepClick: (step: Step) => void;
  className?: string;
  activeColorClass?: string;
};

export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  className = "",
  activeColorClass = "text-orange-501",
}) => (
  <Box className={`h-full ${className}`}>
    {steps.map((step, index) => (
      <StepContainer
        key={step.id || step.stepNo}
        step={step}
        currentStep={currentStep}
        onStepClick={onStepClick}
        index={index + 1}
        activeColorClass={activeColorClass}
      />
    ))}
  </Box>
);

type StepContainerProps = {
  step: Step;
  currentStep: number;
  onStepClick: (step: Step) => void;
  index: number;
  className?: string;
  activeColorClass: string;
};

const StepContainer: React.FC<StepContainerProps> = ({
  step,
  currentStep,
  onStepClick,
  index,
  className = "",
  activeColorClass,
}) => {
  const isCurrent = currentStep === step.stepNo;
  const isCompleted = currentStep > step.stepNo;
  const isDisabled = step.disabled;

  const handleStepClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDisabled) {
      onStepClick(step);
    }
  };

  return (
    <HStack className={`w-full pt-7 ${className} ${!className ? "pl-6" : ""}`}>
      <VStack
        className={`justify-between w-1/5 ${
          isDisabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={handleStepClick}
      >
        <div
          className={`rounded-md shadow-md w-10 h-10 inline-flex justify-center items-center font-medium
            ${
              isCurrent
                ? "text-white bg-orange-501"
                : "bg-opacity-50 text-orange-501"
            }
          `}
        >
          {isCompleted ? (
            <Icon icon="akar-icons:check" className="text-orange-501" />
          ) : (
            step.stepNo
          )}
        </div>
      </VStack>
      <VStack
        className={`w-4/5 justify-evenly ${
          isDisabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={handleStepClick}
      >
        <div
          className={`text-sm2 font-medium text-black-501 ${
            isCurrent ? activeColorClass : ""
          }`}
        >
          {step.title}
        </div>
        <div
          className={`text-xs0 text-gray-401 font-medium ${
            isDisabled ? "cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          {step.description}
        </div>
        {step.children && (
          <div className="">
            {step.children.map((child, childIndex) => (
              <StepContainer
                key={child.id || child.stepNo}
                step={child}
                currentStep={currentStep}
                onStepClick={onStepClick}
                index={childIndex + 1}
                className="pl-0"
                activeColorClass={activeColorClass}
              />
            ))}
          </div>
        )}
      </VStack>
    </HStack>
  );
};
